import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Pause, Play, Volume2, VolumeX, Waves, Heart } from "lucide-react";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";
import { getVoicePref } from "@/lib/voicePref";
import { isSoundEnabled } from "@/lib/chime";
import {
  startBreathAmbience, stopBreathAmbience, duckBreathAmbience,
} from "@/lib/breathAmbience";
import { scaleBeats, scaleToTarget, totalMs, speakMs } from "@/lib/meditationVariants";

/**
 * HeartCoherenceMeditation — a scripted 5-minute guided meditation
 * focused on brain-heart coherence.
 *
 * Structured as a timeline of "beats". Each beat is either:
 *   • { type: "speak",   text }    — narrated in N8's soft meditation voice;
 *                                    the timeline pauses until the utterance
 *                                    finishes so pacing is real, not fake.
 *   • { type: "breathe", cycles }  — a wordless breath section of `cycles`
 *                                    rounds at 5 s inhale / 5 s exhale.
 *   • { type: "pause",   ms }      — a short held silence.
 *
 * Underneath everything, the shared breathing ambience pad plays softly
 * so the read feels held. The whole overlay closes gracefully on tap of
 * the X, and every audio source is torn down on unmount.
 *
 * Total nominal duration: ~5 minutes (300 s). Actual duration depends
 * slightly on the device's TTS voice pace but stays within ±30 s.
 */

const INHALE_S = 5;
const EXHALE_S = 5;

// The full script. Ordered exactly as it should be experienced. Each
// "speak" beat is a full sentence or short paragraph — N8's meditation
// voice will insert breath-level pauses on the commas and periods
// automatically, so this reads slowly and grounded rather than rushed.
const BEATS = [
  { type: "speak", text: "Welcome. Take a moment to settle. Feel the surface beneath you. Let your shoulders soften. Let your jaw release." },
  { type: "pause", ms: 1500 },
  { type: "speak", text: "For the next five minutes, we will guide the mind gently toward the heart. There is nothing to do. Only to notice, and to breathe." },
  { type: "pause", ms: 1500 },
  { type: "speak", text: "We are going to breathe in a slow, steady rhythm. Five seconds in. Five seconds out. Let's begin." },
  { type: "breathe", cycles: 3 },
  { type: "speak", text: "Now bring your attention to the center of your chest. The quiet space around your heart." },
  { type: "pause", ms: 1200 },
  { type: "speak", text: "Imagine that your breath is flowing gently, in and out, through this space. As if the heart itself were breathing." },
  { type: "breathe", cycles: 3 },
  { type: "speak", text: "As you breathe, invite a soft sense of ease into the chest. A quiet feeling of appreciation." },
  { type: "pause", ms: 1200 },
  { type: "speak", text: "It could be a memory. A face. A place you love. Or simply the feeling of being here, in this breath." },
  { type: "breathe", cycles: 3 },
  { type: "speak", text: "Silently, on the next inhale, say the words: I am whole." },
  { type: "breathe", cycles: 2 },
  { type: "speak", text: "I am whole." },
  { type: "pause", ms: 2500 },
  { type: "speak", text: "I am whole." },
  { type: "breathe", cycles: 2 },
  { type: "speak", text: "Feel how the body settles. How the heart holds this rhythm without asking." },
  { type: "pause", ms: 1500 },
  { type: "speak", text: "This coherence is always available to you. It lives here, in the quiet between breaths." },
  { type: "breathe", cycles: 2 },
  { type: "speak", text: "Gently begin to return. Feel your hands. Your feet. The space around you." },
  { type: "pause", ms: 1500 },
  { type: "speak", text: "Take one soft breath of your own. And when you are ready, open your eyes." },
  { type: "pause", ms: 2000 },
  { type: "speak", text: "You are here. You are whole." },
];

// Approximate wall-clock duration of each beat, used only for the
// progress ring at the top of the overlay. Speech beats are estimated
// from word count (≈550 ms/word at N8's meditation pace, plus the
// average pause overhead per sentence).
function beatDurationMs(beat) {
  if (beat.type === "pause") return beat.ms || 0;
  if (beat.type === "breathe") return (beat.cycles || 1) * (INHALE_S + EXHALE_S) * 1000;
  if (beat.type === "speak") return speakMs(beat.text);
  return 0;
}

const TOTAL_MS = BEATS.reduce((acc, b) => acc + beatDurationMs(b), 0);

// Wall-clock targets by variant. `scaleToTarget` computes the pause +
// breath cycle multiplier needed to land here after the fixed cost of
// spoken beats. Written as ms to match the rest of the module.
const TARGETS = {
  beginner:      5 * 60 * 1000,
  intermediate: 10 * 60 * 1000,
  advanced:     20 * 60 * 1000,
};

export default function HeartCoherenceMeditation({ open, onClose, variant = "beginner" }) {
  const target = TARGETS[variant] ?? TARGETS.beginner;
  const beats = React.useMemo(() => scaleToTarget(BEATS, target, variant), [target, variant]);
  const TOTAL_MS_LOCAL = React.useMemo(() => totalMs(beats), [beats]);
  const [beatIdx, setBeatIdx] = useState(0);
  const [inBreath, setInBreath] = useState(false);   // "inhale" phase flag for the ring
  const [breathIndex, setBreathIndex] = useState(0); // 0-based cycle index within a breathe beat
  const [phase, setPhase] = useState("in");          // "in" | "out"
  const [phaseSecondsLeft, setPhaseSecondsLeft] = useState(INHALE_S);
  const [paused, setPaused] = useState(false);
  const [narrating, setNarrating] = useState(() => canSpeak() && getVoicePref().enabled);
  const [ambience, setAmbience] = useState(() => isSoundEnabled());
  const [elapsedMs, setElapsedMs] = useState(0);
  const cancelledRef = useRef(false);
  const timeoutRef = useRef(null);

  // Reset all state when the overlay opens.
  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    setBeatIdx(0);
    setInBreath(false);
    setBreathIndex(0);
    setPhase("in");
    setPhaseSecondsLeft(INHALE_S);
    setPaused(false);
    setElapsedMs(0);
  }, [open]);

  // Cleanup on close/unmount.
  useEffect(() => () => {
    cancelledRef.current = true;
    stopSpeaking();
    stopBreathAmbience();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Ambience lifecycle — fades in on open, ducks on pause, stops on close.
  useEffect(() => {
    if (!open) { stopBreathAmbience(); return undefined; }
    if (!ambience) { stopBreathAmbience(); return undefined; }
    startBreathAmbience();
    return () => { /* handled by the open==false branch */ };
  }, [open, ambience]);

  useEffect(() => {
    if (!ambience || !open) return;
    duckBreathAmbience(paused ? 0.35 : 1.0, 0.6);
  }, [paused, ambience, open]);

  // Drive the beat sequence. On each beatIdx change, run the appropriate
  // handler and schedule advancing to the next beat.
  useEffect(() => {
    if (!open || paused || cancelledRef.current) return undefined;
    const beat = beats[beatIdx];
    if (!beat) return undefined;
    let cleanup = () => {};

    const advance = () => {
      if (cancelledRef.current) return;
      setElapsedMs((ms) => ms + beatDurationMs(beat));
      if (beatIdx + 1 >= beats.length) {
        // Meditation complete — leave the closing line on screen until
        // the member taps X.
        return;
      }
      setBeatIdx(beatIdx + 1);
    };

    if (beat.type === "speak") {
      setInBreath(false);
      if (narrating && canSpeak()) {
        speak(beat.text, { context: "meditation", onEnd: advance });
        cleanup = () => stopSpeaking();
      } else {
        // Fall back to a reading-time pause when narration is muted so the
        // visual pacing still matches what the audio would deliver.
        const dur = beatDurationMs(beat);
        timeoutRef.current = setTimeout(advance, dur);
        cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
      }
    } else if (beat.type === "pause") {
      setInBreath(false);
      timeoutRef.current = setTimeout(advance, beat.ms || 0);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    } else if (beat.type === "breathe") {
      // Run the breath section as a sequence of INHALE_S + EXHALE_S beats
      // driven by 1-second ticks — this lets us update the ring/label
      // every second and pause cleanly.
      setInBreath(true);
      setBreathIndex(0);
      setPhase("in");
      setPhaseSecondsLeft(INHALE_S);
      let cycles = beat.cycles || 1;
      let done = false;
      const tick = () => {
        if (cancelledRef.current || done) return;
        setPhaseSecondsLeft((s) => {
          if (s > 1) {
            timeoutRef.current = setTimeout(tick, 1000);
            return s - 1;
          }
          // Phase transition
          setPhase((p) => {
            if (p === "in") {
              setPhaseSecondsLeft(EXHALE_S);
              timeoutRef.current = setTimeout(tick, 1000);
              return "out";
            }
            // finished one full cycle
            cycles -= 1;
            setBreathIndex((n) => n + 1);
            if (cycles <= 0) {
              done = true;
              setInBreath(false);
              advance();
              return "in";
            }
            setPhaseSecondsLeft(INHALE_S);
            timeoutRef.current = setTimeout(tick, 1000);
            return "in";
          });
          return 1;
        });
      };
      timeoutRef.current = setTimeout(tick, 1000);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    }

    return () => { cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beatIdx, open, paused, narrating]);

  // Global pause — cancel any in-flight speech and freeze the schedule
  // until Resume is tapped, then re-run the current beat.
  useEffect(() => {
    if (paused) {
      stopSpeaking();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [paused]);

  // Voice preference change (member flips it off on Profile mid-flow).
  useEffect(() => {
    const onPref = () => {
      const enabled = getVoicePref().enabled;
      if (!enabled) { setNarrating(false); stopSpeaking(); }
    };
    window.addEventListener("n8-voice-pref-changed", onPref);
    return () => window.removeEventListener("n8-voice-pref-changed", onPref);
  }, []);

  if (!open) return null;

  const beat = beats[beatIdx];
  const finished = beatIdx >= beats.length - 1 && !inBreath && !paused;
  const progress = Math.min(100, Math.round((elapsedMs / TOTAL_MS_LOCAL) * 100));

  const displayText = beat?.type === "speak"
    ? beat.text
    : beat?.type === "breathe"
      ? (phase === "in" ? "Breathe in…" : "Breathe out…")
      : ""; // pause — leave whatever was on screen

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Heart coherence meditation"
      data-testid="heart-coherence-overlay"
    >
      {/* Deep, dim ambient backdrop — same feel as the nightly viz */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(11, 15, 19, 0.985)" }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(ellipse at 50% 42%, rgba(220,180,140,0.14) 0%, rgba(60,40,30,0.05) 45%, transparent 75%)",
        }}
      />

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5" data-testid="heart-coherence-progress-track">
        <div
          className="h-full bg-[hsl(var(--terracotta))]/70 transition-[width] duration-700"
          style={{ width: `${progress}%` }}
          data-testid="heart-coherence-progress-bar"
        />
      </div>

      {/* Close — z-30 so it sits above the main stage (which itself is
          z-10). Without this bump the stage's full-width div intercepts
          pointer events over the X and the button can never receive a
          click. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close meditation"
        data-testid="heart-coherence-close"
        className="absolute top-4 right-4 z-30 rounded-full p-2.5 text-white/80 hover:text-white bg-white/5 hover:bg-white/15 tab-link"
      >
        <X size={18} />
      </button>

      {/* Main stage */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-8 text-center gap-10">
        <BreathRing
          active={inBreath}
          phase={phase}
          secondsLeft={phaseSecondsLeft}
        />

        {/* Instruction / narration text */}
        <div className="max-w-2xl min-h-[3.5em]">
          <p
            key={beatIdx}
            className="font-display italic text-white/90 text-2xl md:text-3xl leading-relaxed animate-[fade-up_800ms_ease-out]"
            data-testid="heart-coherence-text"
          >
            {displayText}
          </p>
        </div>

        {/* Bottom controls */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? "Resume meditation" : "Pause meditation"}
            data-testid="heart-coherence-toggle"
            className="rounded-full p-2 text-white/80 hover:text-white hover:bg-white/10 tab-link"
            disabled={finished}
          >
            {paused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          {canSpeak() && (
            <button
              type="button"
              onClick={() => setNarrating((n) => !n)}
              aria-label={narrating ? "Mute N8's voice" : "Unmute N8's voice"}
              aria-pressed={narrating}
              data-testid="heart-coherence-narration-toggle"
              className={`rounded-full p-2 tab-link ${
                narrating
                  ? "text-white bg-white/15 hover:bg-white/25"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              }`}
              disabled={finished}
            >
              {narrating ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAmbience((a) => !a)}
            aria-label={ambience ? "Mute breathing ambience" : "Play breathing ambience"}
            aria-pressed={ambience}
            data-testid="heart-coherence-ambience-toggle"
            title={ambience ? "Ambience on" : "Ambience off"}
            className={`rounded-full p-2 tab-link ${
              ambience
                ? "text-white bg-white/15 hover:bg-white/25"
                : "text-white/60 hover:text-white hover:bg-white/10"
            }`}
            disabled={finished}
          >
            <Waves size={15} />
          </button>
        </div>

        {finished && (
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/60" data-testid="heart-coherence-complete">
            Meditation complete
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * BreathRing — a soft expanding/contracting ring that visualizes the
 * 5-second inhale / 5-second exhale. When `active` is false the ring
 * sits still at a gentle resting size so the visual doesn't disappear
 * during speech beats — it just breathes very slowly on its own.
 */
function BreathRing({ active, phase, secondsLeft }) {
  const isIn = phase === "in";
  const size = active ? (isIn ? 260 : 160) : 200;
  return (
    <div className="relative grid place-items-center h-[300px] w-[300px]" data-testid="heart-coherence-ring">
      {/* Outer aura */}
      <div
        aria-hidden
        className="absolute rounded-full transition-[width,height,opacity] ease-in-out"
        style={{
          width: size + 60,
          height: size + 60,
          transitionDuration: `${active ? (isIn ? INHALE_S : EXHALE_S) * 1000 : 6000}ms`,
          background: "radial-gradient(circle, rgba(220,180,140,0.22) 0%, rgba(220,180,140,0) 65%)",
          opacity: active ? 1 : 0.55,
        }}
      />
      {/* Ring itself */}
      <div
        aria-hidden
        className="absolute rounded-full border-2 border-[hsl(var(--terracotta))]/70 transition-[width,height] ease-in-out"
        style={{
          width: size,
          height: size,
          transitionDuration: `${active ? (isIn ? INHALE_S : EXHALE_S) * 1000 : 6000}ms`,
        }}
      />
      {/* Heart glyph at center */}
      <Heart
        aria-hidden
        size={28}
        className="text-[hsl(var(--terracotta))]/90"
        strokeWidth={1.5}
      />
      {/* Countdown / cue */}
      {active && (
        <div
          className="absolute bottom-0 text-[10px] uppercase tracking-[0.28em] text-white/70"
          data-testid="heart-coherence-countdown"
        >
          {isIn ? "In" : "Out"} · {secondsLeft}s
        </div>
      )}
    </div>
  );
}
